import { useCurrentWeather } from '../hooks/useWeather'
import { useCalendarWidget } from '../hooks/useCalendar'
import { useNewsWidget } from '../hooks/useNews'

const WeatherWidget = ({ onOpenApp }) => {
  const { weather, loading } = useCurrentWeather()

  // Get weather icon based on condition
  const getWeatherIcon = (condition, icon) => {
    if (loading) return 'fas fa-spinner fa-spin'
    
    switch (condition?.toLowerCase()) {
      case 'clear':
        return 'fas fa-sun text-yellow-400'
      case 'clouds':
        return 'fas fa-cloud text-gray-300'
      case 'rain':
        return 'fas fa-cloud-rain text-blue-400'
      case 'drizzle':
        return 'fas fa-cloud-rain text-blue-300'
      case 'thunderstorm':
        return 'fas fa-bolt text-yellow-300'
      case 'snow':
        return 'fas fa-snowflake text-blue-200'
      case 'mist':
      case 'fog':
        return 'fas fa-smog text-gray-400'
      default:
        return 'fas fa-sun text-yellow-400'
    }
  }

  const handleClick = () => {
    if (onOpenApp) {
      onOpenApp('weather')
    }
  }

  return (
    <div 
      className="glass-effect rounded-2xl p-4 widget hover:bg-white/5 transition-colors cursor-pointer" 
      onClick={handleClick}
      title={loading ? 'Loading weather...' : weather?.isDemo ? 'Demo weather data - Click for settings' : 'Click to open Weather app'}
    >
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">
            {loading ? (
              <div className="flex items-center space-x-2">
                <i className="fas fa-spinner fa-spin text-sm"></i>
                <span>Loading...</span>
              </div>
            ) : (
              weather?.location || 'Weather'
            )}
          </h3>
          <p className="text-sm text-white/70">
            {loading ? 'Getting location...' : (weather?.condition || 'Unknown')}
          </p>
          {weather?.isDemo && (
            <p className="text-xs text-pink-400 mt-1">Demo Mode</p>
          )}
        </div>
        <i className={`${getWeatherIcon(weather?.condition, weather?.icon)} text-2xl`}></i>
      </div>
      <div className="flex justify-between mt-4">
        <div className="text-center">
          <p className="text-3xl font-light">
            {loading ? '--' : `${weather?.temperature || '--'}°`}
          </p>
          <p className="text-xs text-white/60">Current</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-light">
            {loading ? '--' : `${weather?.high || '--'}°`}
          </p>
          <p className="text-xs text-white/60">High</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-light">
            {loading ? '--' : `${weather?.low || '--'}°`}
          </p>
          <p className="text-xs text-white/60">Low</p>
        </div>
      </div>
    </div>
  )
}

const CalendarWidget = ({ onOpenApp }) => {
  const { widgetEvents, loading } = useCalendarWidget()
  const today = new Date()
  const todayFormatted = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  const handleClick = () => {
    if (onOpenApp) {
      onOpenApp('calendar')
    }
  }

  const getEventIcon = (color) => {
    switch (color) {
      case 'blue': return '🔵'
      case 'pink': return '🟣' 
      case 'purple': return '🟣'
      default: return '📅'
    }
  }

  const getEventBgColor = (color) => {
    switch (color) {
      case 'blue': return 'bg-blue-500/20'
      case 'pink': return 'bg-pink-500/20'
      case 'purple': return 'bg-purple-500/20'
      default: return 'bg-blue-500/20'
    }
  }

  return (
    <div 
      className="glass-effect rounded-2xl p-4 widget hover:bg-white/5 transition-colors cursor-pointer" 
      onClick={handleClick}
      title="Click to open Calendar app with historical events"
    >
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">On This Day</h3>
        <span className="text-sm text-white/70">{today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      </div>
      
      <div className="mt-4">
        {loading ? (
          <div className="flex items-center space-x-2">
            <i className="fas fa-spinner fa-spin text-sm"></i>
            <span className="text-sm text-white/70">Loading history...</span>
          </div>
        ) : (
          widgetEvents.slice(0, 2).map((event, index) => (
            <div key={index} className={`flex items-center ${index > 0 ? 'mt-3' : ''}`}>
              <div className={`w-8 h-8 rounded-full ${getEventBgColor(event.color)} flex items-center justify-center mr-3 flex-shrink-0`}>
                <span className="text-lg">{event.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{event.title}</p>
                <p className="text-xs text-white/60 leading-relaxed">{event.description}</p>
                <p className="text-xs text-white/40 mt-1">{event.time}</p>
              </div>
            </div>
          ))
        )}
        
        {!loading && widgetEvents.length === 0 && (
          <div className="text-center text-white/60">
            <i className="fas fa-calendar-alt text-2xl mb-2"></i>
            <p className="text-sm">Click to explore history!</p>
          </div>
        )}
      </div>
    </div>
  )
}

const NewsWidget = ({ onOpenApp }) => {
  const { widgetNews, loading } = useNewsWidget()

  const handleClick = () => {
    if (onOpenApp) {
      onOpenApp('news')
    }
  }

  const handleArticleClick = (e, url) => {
    e.stopPropagation()
    if (url && url !== '#') {
      window.open(url, '_blank')
    }
  }

  return (
    <div 
      className="glass-effect rounded-2xl p-4 widget hover:bg-white/5 transition-colors cursor-pointer"
      onClick={handleClick}
      title="Click to open News app with more articles"
    >
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold flex items-center">
          <i className="fas fa-newspaper mr-2 text-orange-400"></i>
          Dev News
        </h3>
        <span className="text-xs text-white/50">Hacker News</span>
      </div>
      
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center space-x-2">
            <i className="fas fa-spinner fa-spin text-sm"></i>
            <span className="text-sm text-white/70">Loading news...</span>
          </div>
        ) : (
          widgetNews.slice(0, 3).map((article, index) => (
            <div 
              key={index} 
              className="group hover:bg-white/10 rounded-lg p-2 -m-2 transition-colors cursor-pointer"
              onClick={(e) => handleArticleClick(e, article.url)}
              title={`Click to read: ${article.title}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-2">
                  <p className="text-sm leading-relaxed text-white/90 group-hover:text-white">
                    {article.title}
                  </p>
                  <div className="flex items-center mt-1 space-x-2">
                    <span className="inline-block bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs">
                      {article.description}
                    </span>
                    <span className="text-xs text-white/50">{article.timeAgo}</span>
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center space-x-1">
                  {article.score && (
                    <span className="flex items-center text-xs text-orange-400">
                      <i className="fas fa-arrow-up mr-1"></i>
                      {article.score}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        
        {!loading && widgetNews.length === 0 && (
          <div className="text-center text-white/60">
            <i className="fas fa-newspaper text-2xl mb-2"></i>
            <p className="text-sm">Click to browse tech news!</p>
          </div>
        )}
      </div>
    </div>
  )
}

const Widgets = ({ onOpenApp }) => {
  return (
    <div className="hidden md:grid absolute top-20 lg:top-32 right-4 lg:right-6 gap-3 lg:gap-4 w-72 lg:w-80 z-0 pointer-events-auto">
      <WeatherWidget onOpenApp={onOpenApp} />
      <CalendarWidget onOpenApp={onOpenApp} />
      <NewsWidget onOpenApp={onOpenApp} />
    </div>
  )
}

export default Widgets